import React from 'react'
import { styled, FloraVariants, forwardRef } from '@grupoboticario/flora-react-system'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

const StyledCard = styled('div', {
  appearance: 'none',
  border: 'none',
  boxSizing: 'border-box',
  font: 'inherit',
  lineHeight: '1',
  outline: 'none',
  textAlign: 'inherit',
  verticalAlign: 'middle',
  WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',

  backgroundColor: '$backgroundPrimary',
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
  flexShrink: 0,
  borderRadius: '$medium',
  position: 'relative',
  padding: '$3',

  '&::before': {
    boxSizing: 'border-box',
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    boxShadow: 'inset 0 0 0 1px rgb(180, 180, 180, .3)',
    borderRadius: '$medium',
    pointerEvents: 'none',
  },

  variants: {
    padded: {
      false: {
        padding: 0,
      },
    },
    variant: {
      interactive: {
        cursor: 'pointer',
        '@hover': {
          '&:hover': {
            '&::before': {
              boxShadow: 'inset 0 0 0 1px rgb(180, 180, 180, .7)',
            },
          },
        },
        '&:focus': {
          '&::before': {
            boxShadow:
              'inset 0 0 0 1px $colors$backgroundAltPrimary, 0 0 0 1px $colors$backgroundAltPrimary',
          },
        },
      },
      ghost: {
        cursor: 'pointer',
        backgroundColor: 'transparent',
        transition: 'transform 200ms cubic-bezier(0.22, 1, 0.36, 1), background-color 25ms linear',
        willChange: 'transform',
        '&::before': {
          boxShadow: '$downFar',
          opacity: '0',
          transition: 'all 200ms cubic-bezier(0.22, 1, 0.36, 1)',
        },
        '@hover': {
          '&:hover': {
            backgroundColor: '$background',
            transform: 'translateY(-2px)',
            '&::before': {
              opacity: '1',
            },
          },
        },
        '&:active': {
          transform: 'translateY(0)',
          transition: 'none',
          '&::before': {
            boxShadow: '$downClose',
            opacity: '1',
          },
        },
        '&:focus': {
          boxShadow:
            'inset 0 0 0 1px $colors$backgroundAltPrimary, 0 0 0 1px $colors$backgroundAltPrimary',
        },
      },
      active: {
        cursor: 'pointer',
        transform: 'translateY(0)',
        transition: 'none',
        '&::before': {
          boxShadow: '$downMid',
          opacity: '1',
        },
        '&:focus': {
          boxShadow:
            'inset 0 0 0 1px $colors$backgroundAltPrimary, 0 0 0 1px $colors$backgroundAltPrimary',
        },
      },
    },
  },
})

export type CardProps = HTMLFloraProps<'div'> & FloraVariants<typeof StyledCard>

/**
 * @component
 * @name Card
 * @description Card component
 * @tutorial https://flora.gb.tech/docs/components/core/card
 */
export const Card = forwardRef<'div', CardProps>((props, forwardedRef) => {
  return <StyledCard {...props} data-flora="card" ref={forwardedRef} />
})
